import React, { useState, useEffect, useContext } from "react";
import { GlobalContext } from "../../../App";
import L from "leaflet";
import * as turf from "@turf/turf";
import { EditableGeoJsonLayer, ViewMode } from "nebula.gl";

export default function SelectByLocation() {
  const {
    vis,
    layerControls,
    map,
    Canvas,
    drawnItems,
    lastRect,
    filLayer,
    setFilLayer,
    setLayerData,
    highlightedId, setHighlightedId,
    highlightedIdFeature, setHighlightedIdFeature
  } = useContext(GlobalContext);
  const [show, setShow] = useState(false);
  const [layers, setLayers] = useState([]);
  const [loader, setLoader] = useState(false);
  const [inputLayer, setInputLayer] = useState("");
  const [compareLayer, setCompareLayer] = useState("");
  const [addOnMap, setAddOnMap] = useState(false);

  function handleClick() {
    setShow((prevShow) => !prevShow);
    if (Canvas) {
      let list = Canvas.getLayers();
      setLayers(list);
      Canvas.on("layerchange", () => {
        let list = Canvas.getLayers();
        if (list.includes(filLayer)) {
          setLayers(list);
        } else {
          if (list) {
            setLayers(list);
          } else {
            setFilLayer("");
            setLayers([]);
          }
        }
      });
      Canvas.on("loading", () => {
        setLoader(true);
      });
    }
    console.log("info", "Filter panel opened");
  }

  function close() {
    setShow(false);
  }

  const validateLayers = (action) => {
    if (!inputLayer || !compareLayer) {
      alert("Please select both layers.");
      return false;
    }
    if (inputLayer === compareLayer) {
      alert("The selected layers must be different.");
      return false;
    }

    const layerName = prompt(`Enter a name for the new layer:`);
    if (layerName) {
      return layerName;
    }

    return false;
  };

  const handleSelectByLocation = () => {
    const [layer1, layer2] = [
      Canvas.getLayerData(inputLayer),
      Canvas.getLayerData(compareLayer),
    ];
    if (!layer1 || !layer2)
      return alert("One or both layers do not contain valid data.");

    const resp = Canvas.VectorPointInPolygonIntersect(
      inputLayer,
      compareLayer,
      "Point-In-Polygon",
      false
    );

    if (!resp || !resp[0]) return alert("Point-in-polygon operation failed.");

    console.log("resp", resp);

    const highlightedFeatures = {
      ...resp[2],
      features: resp[2].features.map((f) => ({
        ...f,
        properties: {
          ...f.properties,
          selection: true,
        },
      })),
    };
    console.log('highlightedFeatures', highlightedFeatures);
    setHighlightedId(resp[0]);
    setHighlightedIdFeature(highlightedFeatures);
  };

  const handleAddToLayerControl = () => {
    try {
      const valid = validateLayers("Point-In-Polygon");
      if (!valid) return;

      if (!inputLayer || !compareLayer)
        return alert("One or both layers do not contain valid data.");

      const resp = Canvas.VectorPointInPolygonIntersect(
        inputLayer,
        compareLayer,
        "Point-In-Polygon",
        true,
      );
      if (!resp || !resp[0]) return alert("Operation failed.");

      const layerBounds = L.latLngBounds(
        L.latLng(resp[1][1], resp[1][0]),
        L.latLng(resp[1][3], resp[1][2])
      );
      console.log("layerBounds", layerBounds);

      layerControls.addOverlay(
        L.geoJSON(),
        valid,
        true,
        layerBounds,
        true,
        resp[0],
        false,
        null
      );

      alert(`Layer "${valid}" added to layer control.`);
    } catch (error) {
      console.error("Error in handleSymmetricDifference:", error);
      alert(
        `An error occurred during Symmetric Difference operation: ${error.message}`
      );
    }
  };

  const handleDehighlight = () => {
    if (!highlightedId) return alert("No highlighted layer to remove.");

    Canvas.deHighlightLayer(highlightedId);

    setHighlightedId(null);
    setHighlightedIdFeature(null);

    setLayerData((prev) => {
      if (!prev) return prev;
      const cleared = {
        ...prev,
        features: prev.features.map((f) => ({
          ...f,
          properties: {
            ...f.properties,
            selection: false,
          },
        })),
      };
      if (prev.name) {
        Canvas.updateLayerData(prev.name, cleared);
      }
      return cleared;
    });
  };

  return (
    <>
      <div
        style={{ position: vis ? "absolute" : "relative" }}
        className="toolscont"
      >
        {/* ── Trigger Button ── */}
        <button
          title="Select By Location"
          className="btn text-white"
          onClick={handleClick}
          style={{
            backgroundColor: "black",
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            border: "none",
          }}
        >
          <i className="fa-solid fa-map-pin" />
        </button>

        {show && (
          <div
            className="toolsvis"
            id="toolsvis"
            style={{ display: "flex", flexDirection: "column", width: "220px", padding: "10px 12px" }}
          >
            {/* ── Header ── */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingBottom: "8px",
              marginBottom: "10px",
              borderBottom: "1px solid rgba(255,255,255,0.12)",
            }}>
              <span style={{ color: "#ccc", fontSize: "13px", fontWeight: 600 }}>
                <i className="fa-solid fa-map-pin" style={{ marginRight: "6px", fontSize: "11px" }} />
                Select by Location
              </span>
              <span
                onClick={close}
                style={{ color: "#e74c3c", fontSize: "20px", cursor: "pointer", lineHeight: 1 }}
              >
                &times;
              </span>
            </div>

            {/* ── Body ── */}
            {map && (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>

                <div className="toolsvis-body">
                  <select
                    className="form-select"
                    style={{
                      backgroundColor: "transparent",
                      border: "1px solid rgba(255,255,255,0.25)",
                      fontSize: "12px",
                      color: "white",
                    }}
                    value={inputLayer}
                    onChange={(e) => setInputLayer(e.target.value)}
                  >
                    <option value="">Select Features from</option>
                    {layers.map((key) => (
                      <option key={key} value={key}>{key}</option>
                    ))}
                  </select>
                </div>

                <div className="toolsvis-body">
                  <select
                    className="form-select"
                    style={{
                      backgroundColor: "transparent",
                      border: "1px solid rgba(255,255,255,0.25)",
                      fontSize: "12px",
                      color: "white",
                    }}
                    value={compareLayer}
                    onChange={(e) => setCompareLayer(e.target.value)}
                  >
                    <option value="">By comparing to features from</option>
                    {layers.map((key) => (
                      <option key={key} value={key}>{key}</option>
                    ))}
                  </select>
                </div>

                <button
                  style={{ width: "100%" }}
                  onClick={handleSelectByLocation}
                >
                  Apply
                </button>

                <button
                  style={{ width: "100%", backgroundColor: "rgba(255,255,255,0.1)" }}
                  onClick={handleDehighlight}
                >
                  Remove Highlight
                </button>

                <button
                  style={{ width: "100%", backgroundColor: "rgba(30,120,60,0.55)" }}
                  onClick={handleAddToLayerControl}
                >
                  Add to Layer Control
                </button>

              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}